import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  LATEST_UPDATE_VERSION,
  UPDATES_LIST,
  getUpdateLink,
} from "./updatesConfig";
import "./WhatsNew.scss";

// Only a plain version string (for example "v1.1.0") is stored. No user data.
// sessionStorage = the dot comes back each new browser session.
// Change to window.localStorage if you want the dot to show only once per device.
const SEEN_KEY = "crm_last_seen_update_version";
const storage = () => window.sessionStorage;

const WhatsNew = () => {
  const { hasRole } = useAuth();
  const [open, setOpen] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    try {
      setHasNew(storage().getItem(SEEN_KEY) !== LATEST_UPDATE_VERSION);
    } catch {
      setHasNew(false); // storage can be blocked in private modes
    }
  }, []);

  const openMenu = () => {
    setOpen(true);
    if (!hasNew) return;
    setHasNew(false);
    try {
      storage().setItem(SEEN_KEY, LATEST_UPDATE_VERSION);
    } catch {
      /* ignore */
    }
  };

  // Close on outside click or Escape (helps on touch screens, where there is no hover).
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target))
        setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      className="wn"
      ref={rootRef}
      onMouseEnter={openMenu}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="wn-button"
        aria-label="What's new"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={openMenu}
      >
        <Icon icon="mdi:history" />
        {hasNew && <span className="wn-dot" aria-label="New updates" />}
      </button>

      {open && (
        <div className="wn-menu-wrap">
          <div className="wn-menu" role="menu">
            <div className="wn-head">
              <h4>What's new</h4>
            </div>
            <div className="wn-list">
              {UPDATES_LIST.slice(0, 3).map((u) => {
                const link = getUpdateLink(u, hasRole);
                return (
                  <article className="wn-item" key={u.version}>
                    <div className="wn-item-top">
                      <span className="wn-version">{u.version}</span>
                      <span className="wn-date">{u.date}</span>
                    </div>
                    <strong>{u.title}</strong>
                    <p>{u.description}</p>
                    {link && (
                      <Link
                        to={link.to}
                        className="wn-try"
                        onClick={() => setOpen(false)}
                      >
                        {link.label} <Icon icon="mdi:arrow-right" />
                      </Link>
                    )}
                  </article>
                );
              })}
            </div>
            <Link
              to="/updates"
              className="wn-all"
              onClick={() => setOpen(false)}
            >
              View all updates
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsNew;
